import { createId } from "../../app/runtimeContext";
import type { CyanIntelligenceRepository } from "./repositories";
import type { Decision, DecisionOption } from "./types";

export class DecisionEngine {
  constructor(private readonly repository: CyanIntelligenceRepository) {}

  score(option: DecisionOption): number {
    const positive =
      option.benefit * 0.22 +
      option.confidence * 0.16 +
      option.urgency * 0.14 +
      option.strategicValue * 0.2;
    const negative =
      option.cost * 0.1 +
      option.risk * 0.1 +
      option.dependencies * 0.04 +
      option.technicalDebt * 0.04;
    return Number((positive - negative).toFixed(4));
  }

  async decide(input: {
    organizationId: string;
    question: string;
    options: DecisionOption[];
  }): Promise<Decision> {
    if (input.options.length === 0) {
      throw new Error("DecisionEngine requires at least one option.");
    }
    const scored = input.options
      .map((option) => ({ ...option, score: this.score(option) }))
      .sort((a, b) => b.score - a.score);
    const selected = scored[0];
    const decision: Decision = {
      id: createId("decision"),
      organizationId: input.organizationId,
      question: input.question,
      selectedOption: selected,
      score: selected.score,
      explanation: `${selected.label} selected because weighted benefit, confidence, urgency, and strategic value exceeded cost, risk, dependencies, and technical debt.`,
      options: scored,
      createdAt: new Date().toISOString(),
    };
    await this.repository.saveDecision(decision);
    return decision;
  }
}
